import React from 'react';
import { ArrowDown, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/base/Card';
import { ConsultationScrollArea } from '@/components/consultation/ConsultationScrollArea';
import { ReferenceSlokasModal } from '@/ayurveda/components/ReferenceSlokasModal';
import type { ReferenceSlokaItem } from '@/ayurveda/types/ayurveda';

export interface LabReportChip {
  label: string;
  flag: 'down' | 'up' | 'normal';
}

export interface PranikAIPanelProps {
  diagnoses: string[];
  referenceSlokas?: ReferenceSlokaItem[];
  aiSearchSummary?: string | null;
  labReports?: LabReportChip[];
}

const DEFAULT_LAB_REPORTS: LabReportChip[] = [];

/** Exact Figma "sparkles" icon used in the Pranik AI header. */
function PranikSparkleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.5303 4.77722L20.7924 5.22763C20.8528 5.24889 20.9054 5.29003 20.9426 5.34517C20.9799 5.40031 21 5.46666 21 5.53473C21 5.60281 20.9799 5.66915 20.9426 5.7243C20.9054 5.77944 20.8528 5.82058 20.7924 5.84183L19.5303 6.29225C19.4858 6.30811 19.4453 6.33487 19.4121 6.37041C19.3789 6.40595 19.3539 6.44929 19.3391 6.49698L18.9194 7.84714C18.8996 7.91182 18.8612 7.96813 18.8097 8.00805C18.7582 8.04798 18.6962 8.06947 18.6326 8.06947C18.569 8.06947 18.5071 8.04798 18.4556 8.00805C18.4041 7.96813 18.3657 7.91182 18.3458 7.84714L17.9251 6.4959C17.9103 6.44821 17.8853 6.40487 17.8521 6.36933C17.8189 6.33379 17.7785 6.30703 17.7339 6.29117L16.4729 5.84183C16.4125 5.82058 16.3599 5.77944 16.3226 5.7243C16.2853 5.66915 16.2653 5.60281 16.2653 5.53473C16.2653 5.46666 16.2853 5.40031 16.3226 5.34517C16.3599 5.29003 16.4125 5.24889 16.4729 5.22763L17.7349 4.77722C17.7795 4.76136 17.8199 4.7346 17.8531 4.69906C17.8863 4.66352 17.9113 4.62018 17.9261 4.57249L18.3458 3.22232C18.3657 3.15765 18.4041 3.10134 18.4556 3.06142C18.5071 3.02149 18.569 3 18.6326 3C18.6962 3 18.7582 3.02149 18.8097 3.06142C18.8612 3.10134 18.8996 3.15765 18.9194 3.22232L19.3401 4.57356C19.3703 4.67054 19.4408 4.74597 19.5313 4.7783M9.77129 4.50891C10.0309 3.67382 11.134 3.67489 11.3936 4.50891L12.9404 9.47639L17.5799 11.1326C18.3589 11.4106 18.3589 12.5916 17.5799 12.8696L12.9404 14.5258L11.3936 19.4932C11.134 20.3273 10.0309 20.3273 9.77129 19.4932L8.22446 14.5258L3.58497 12.8696C2.80501 12.5916 2.80501 11.4106 3.58497 11.1326L8.22446 9.47639L9.77129 4.50891ZM10.5814 7.01312L9.5519 10.319C9.50989 10.4537 9.43919 10.5761 9.34539 10.6766C9.2516 10.777 9.13728 10.8527 9.01146 10.8977L5.92384 12L9.01146 13.1023C9.13728 13.1473 9.2516 13.223 9.34539 13.3234C9.43919 13.4239 9.50989 13.5463 9.5519 13.681L10.5814 16.9869L11.611 13.681C11.653 13.5463 11.7237 13.4239 11.8175 13.3234C11.9113 13.223 12.0256 13.1473 12.1514 13.1023L15.2391 12L12.1514 10.8977C12.0256 10.8527 11.9113 10.777 11.8175 10.6766C11.7237 10.5761 11.653 10.4537 11.611 10.319L10.5814 7.01312ZM20.7924 18.1582L19.5303 17.7078C19.4858 17.6919 19.4453 17.6651 19.4121 17.6296C19.3789 17.594 19.3539 17.5507 19.3391 17.503L18.9194 16.1529C18.8996 16.0882 18.8612 16.0319 18.8097 15.9919C18.7582 15.952 18.6962 15.9305 18.6326 15.9305C18.569 15.9305 18.5071 15.952 18.4556 15.9919C18.4041 16.0319 18.3657 16.0882 18.3458 16.1529L17.9251 17.5041C17.9103 17.5518 17.8853 17.5951 17.8521 17.6307C17.8189 17.6662 17.7785 17.693 17.7339 17.7088L16.4729 18.1582C16.4125 18.1794 16.3599 18.2206 16.3226 18.2757C16.2853 18.3308 16.2653 18.3972 16.2653 18.4653C16.2653 18.5333 16.2853 18.5997 16.3226 18.6548C16.3599 18.71 16.4125 18.7511 16.4729 18.7724L17.7349 19.2228C17.8255 19.2551 17.8959 19.3305 17.9261 19.4275L18.3458 20.7777C18.3657 20.8424 18.4041 20.8987 18.4556 20.9386C18.5071 20.9785 18.569 21 18.6326 21C18.6962 21 18.7582 20.9785 18.8097 20.9386C18.8612 20.8987 18.8996 20.8424 18.9194 20.7777L19.3401 19.4264C19.3549 19.3787 19.3799 19.3354 19.4131 19.2999C19.4463 19.2643 19.4868 19.2376 19.5313 19.2217L20.7924 18.7724C20.8528 18.7511 20.9054 18.71 20.9426 18.6548C20.9799 18.5997 21 18.5333 21 18.4653C21 18.3972 20.9799 18.3308 20.9426 18.2757C20.9054 18.2206 20.8528 18.1794 20.7924 18.1582Z"
        fill="#303E70"
      />
    </svg>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-[#F8F8F1] px-4 py-1">
      <span className="font-hanken text-base font-medium text-[#42434B]">{label}:</span>
      {children}
    </div>
  );
}

export function PranikAIPanel({
  diagnoses,
  referenceSlokas = [],
  aiSearchSummary,
  labReports = DEFAULT_LAB_REPORTS,
}: PranikAIPanelProps) {
  return (
    <Card padding="none" className="h-full overflow-hidden rounded-[24px] border-white bg-white">
      <ConsultationScrollArea className="h-full">
        <CardContent className="flex flex-col gap-4 py-4 pl-4 pr-2">
          <div className="flex flex-col gap-1 rounded-2xl border border-white bg-gradient-to-r from-[#fcdbfa] via-[#eee0d5] via-[52.885%] to-[#a3b28b] px-4 py-2">
            <div className="flex items-center gap-2">
              <PranikSparkleIcon />
              <h2 className="font-serif text-[calc(20px*var(--app-scale))] font-medium leading-[30px] text-[#3C4F1E]">
                Pranik&rsquo;s CDSS
              </h2>
            </div>
            <p className="font-hanken text-sm font-medium tracking-[-0.16px] text-[#8D90A1]">
              Review the kiosk findings and provisional assessment.
            </p>
          </div>

          <Section label="Suggestive Differentials">
            {diagnoses.length > 0 ? (
              <p className="text-sm text-[#585859]">{diagnoses.join(' · ')}</p>
            ) : (
              <p className="text-sm italic text-muted-foreground">No items yet</p>
            )}
          </Section>

          <Section label="Suggestive Slokas">
            {referenceSlokas.length > 0 ? (
              <ReferenceSlokasModal slokas={referenceSlokas} />
            ) : (
              <p className="text-sm italic text-muted-foreground">No items yet</p>
            )}
          </Section>

          <Section label="AI Search">
            <p className="text-sm text-[#585859]">
              {aiSearchSummary || 'No AI search results yet'}
            </p>
          </Section>

          <Section label="Lab Reports Analysis">
            {labReports.length > 0 ? (
              <div className="flex w-full flex-wrap items-center justify-between gap-2">
                {labReports.map((report) => (
                  <span
                    key={report.label}
                    className="flex items-center gap-1 text-sm text-[#585859]"
                  >
                    {report.label}
                    {report.flag === 'down' && <ArrowDown size={16} className="text-[#D35454]" />}
                    {report.flag === 'up' && (
                      <ArrowDown size={16} className="rotate-180 text-[#D35454]" />
                    )}
                    {report.flag === 'normal' && <Check size={16} className="text-[#129E54]" />}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">No items yet</p>
            )}
          </Section>
        </CardContent>
      </ConsultationScrollArea>
    </Card>
  );
}
